import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@material-ui/data-grid';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography'


function ChangeColor({value, changePercentColor} :any) {
  return(
    <div style={{ color: changePercentColor }}>
      <Typography>{ value }</Typography>
    </div>
  );
}



function CompanyListDataGrid({data, storageScopeKey, showListing = false} :any) {

  const STORAGE_KEY = (() => {
    const base = 'di.search.company_list';
    const scope =
      storageScopeKey ??
      (typeof window !== 'undefined' ? window.location.pathname : 'default');
    return `${base}.${scope}`;
  })();
  const [searchTerm, setSearchTerm] = useState(() => {
    try {
      if (typeof window === 'undefined') return '';
      return window.localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const trimmed = searchTerm.trim();
      if (!trimmed) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, searchTerm);
      }
    } catch {
      // ignore localStorage failures (private mode / blocked storage)
    }
  }, [searchTerm]);

  function truncateStr(str, n){
    return (str.length > n) ? str.substr(0, n-1) + '...' : str;
  };


  function recommendationClass(decision) {
    if (decision == 'Overvalued') {
      return <Chip label="Sell" color="secondary"/>
    } else if (decision == 'Undervalued') {
      return <Chip label="Buy" color="primary"/>
    } else if (decision == 'Unpredictable') {
      return <Chip label="Avoid" color="secondary"/>
    } else if (decision == 'Undetermined') {
      return <Chip label="Avoid" color="secondary"/>
    } else {
      return <Chip label="Hold" color="default"/>
    }
  }

  function tableRowClass(decision_color) {
    if (decision_color == 'red') {
      return 'table-danger'
    } else if (decision_color == 'green') {
      return 'table-success'
    } else if (decision_color == null) {
      return 'table-danger'
    } else {
      return 'table-secondary'
    }
  }


  const columns = [
    { field: 'id', headerName: 'Ticker', width: 80, sortable: false, renderHeader:(params) => ( <strong>Ticker</strong>), renderCell: (params) => (<a target="_blank" rel="noopener noreferrer" href={"/ticker/" + params.value}><strong>{params.value}</strong></a>)},
    { field: 'company', headerName: 'Company', width: 200, sortable: false, renderHeader:(params) => ( <strong>Company</strong>)},
    ...(showListing ? [{ field: 'listing', headerName: 'Listing', width: 120, renderHeader:() => ( <strong title="Primary listing exchange">Listing</strong>)}] : []),
    { field: 'industry', headerName: 'Industry', width: 250, renderHeader:(params) => ( <strong>Industry</strong>)},
    { field: 'valueScore', headerName: 'Value Score', width: 127, renderHeader:(params) => ( <strong>Value Score</strong>)},
    { field: 'consideration', headerName: 'Consideration', width: 150, renderHeader:(params) => ( <strong>Consideration</strong>)},
    { field: 'decision', headerName: 'Decision', width: 180, renderHeader:(params) => ( <strong>Decision</strong>)},
    { field: 'price', headerName: 'Price',  width: 100, renderHeader:(params) => ( <strong>Price</strong>), renderCell: (params) => (< ChangeColor value={params.value} changePercentColor={ params.getValue(params.id, 'changePercentColor') } /> )},
    { field: 'changePercent', headerName: 'changePercent', width: 100, renderHeader:(params) => ( <strong>Change</strong>), renderCell: (params) => (< ChangeColor value={params.value} changePercentColor={ params.getValue(params.id, 'changePercentColor') } /> )},
    { field: 'marginOfSafety', headerName: 'Margin of Safety', sortable: false,  width: 180, renderHeader:(params) => ( <strong>Margin of Safety</strong>)},
    { field: 'paybackTime', headerName: 'Payback Time', width: 150, renderHeader:(params) => ( <strong>Payback Time</strong>), renderCell: (params) => (params.value ? (params.value + ' years') : 'unpredictable')},
    { field: 'recommendationColor', hide: true},
    { field: 'changePercentColor', hide: true}
  ]

  function buildCompanyData(item) {
    return {
      id: item.ticker,
      company: item.company,
      listing: item.stock_exchange_symbol || '—',
      industry: item.industry,
      price: item.default_valuation?.price_in_words,
      changePercent: item.quote?.change_percent,
      consideration: item.default_valuation?.recommendation || "Don't Buy",
      decision: item.default_valuation?.decision || 'Undetermined',
      marginOfSafety: item.default_valuation?.margin_of_safety_in_words,
      paybackTime: (item.default_valuation?.payback_time?.current_payback_time_number),
      valueScore: item.value_score,
      recommendationColor: item.default_valuation?.recommendation_color,
      changePercentColor: item.quote?.change_percent_color
    }
  }

  const rows = useMemo(() => data?.map(item => buildCompanyData(item)) ?? [], [data]);

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) => {
      const searchable = [
        row.id,
        row.company,
        row.listing,
        row.industry,
        row.consideration,
        row.decision,
        row.price,
        row.changePercent,
        row.marginOfSafety,
        row.paybackTime,
        `${row.valueScore ?? ''}`,
      ];

      return searchable.some((field) =>
        (field ?? '').toString().toLowerCase().includes(query)
      );
    });
  }, [rows, searchTerm]);

  return (
    <div className="row">
      <style>{`
        .grid-card {
          width: 100%;
          background: #ffffff;
          border-radius: 1rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border: 1px solid #f3f4f6;
          overflow: hidden;
        }

        .grid-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(to right, #f9fafb, #f3f4f6);
        }

        .search-box {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 9999px;
          padding: 0.5rem 1rem 0.5rem 2.75rem;
          transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
        }

        .search-box:focus-within {
          border-color: #2563eb;
          box-shadow: 0 10px 25px -10px rgba(37, 99, 235, 0.35);
          background: #ffffff;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          width: 1rem;
          height: 1rem;
          color: #6b7280;
        }

        .search-field {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.95rem;
          color: #111827;
        }

        .search-field::placeholder {
          color: #9ca3af;
        }

        .search-clear {
          background: none;
          border: none;
          color: #6b7280;
          font-weight: 600;
          cursor: pointer;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          transition: background-color 0.15s, color 0.15s;
        }

        .search-clear:hover {
          background-color: #f3f4f6;
          color: #111827;
        }

        .search-meta {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: #4b5563;
          white-space: nowrap;
        }

        .search-meta strong {
          color: #111827;
        }

        .company-list-grid .MuiDataGrid-root {
          border: none;
        }

        .company-list-grid .MuiDataGrid-columnHeaders {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .company-list-grid .MuiDataGrid-columnHeader,
        .company-list-grid .MuiDataGrid-cell {
          border-bottom: 1px solid #f3f4f6;
        }

        .company-list-grid .MuiDataGrid-cell {
          color: #111827;
        }

        .company-list-grid .MuiDataGrid-cell:focus,
        .company-list-grid .MuiDataGrid-columnHeader:focus,
        .company-list-grid .MuiDataGrid-cell:focus-within,
        .company-list-grid .MuiDataGrid-columnHeader:focus-within {
          outline: none;
        }

        .company-list-grid .MuiDataGrid-row:hover {
          background: #f9fafb;
        }

        .company-list-grid .MuiDataGrid-footerContainer {
          border-top: 1px solid #e5e7eb;
          background: #ffffff;
        }

        .company-list-grid .MuiTablePagination-root,
        .company-list-grid .MuiTablePagination-caption,
        .company-list-grid .MuiTablePagination-selectIcon,
        .company-list-grid .MuiIconButton-root {
          color: #4b5563;
        }

        .company-list-grid .MuiChip-root {
          font-weight: 600;
        }

        .dark-mode .grid-card {
          background: #222328;
          border-color: #322f2f;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.22), 0 10px 10px -5px rgba(0, 0, 0, 0.18);
        }

        .dark-mode .grid-toolbar {
          background: linear-gradient(to right, #1a191d, #222328);
          border-bottom-color: #322f2f;
        }

        .dark-mode .search-box {
          background: #151519;
          border-color: #322f2f;
        }

        .dark-mode .search-box:focus-within {
          border-color: #b66dff;
          box-shadow: 0 10px 25px -10px rgba(182, 109, 255, 0.35);
          background: #222328;
        }

        .dark-mode .search-icon,
        .dark-mode .search-clear,
        .dark-mode .search-field::placeholder,
        .dark-mode .search-meta {
          color: #9c9fa6;
        }

        .dark-mode .search-field,
        .dark-mode .search-meta strong {
          color: #ffffff;
        }

        .dark-mode .search-clear:hover {
          background-color: #1a191d;
          color: #ffffff;
        }

        .dark-mode .company-list-grid .MuiDataGrid-root {
          color: #ffffff;
          background: #222328;
        }

        .dark-mode .company-list-grid .MuiDataGrid-columnHeaders {
          background: linear-gradient(to right, #1a191d, #222328);
          border-bottom-color: #322f2f;
        }

        .dark-mode .company-list-grid .MuiDataGrid-columnHeader,
        .dark-mode .company-list-grid .MuiDataGrid-cell {
          border-bottom-color: #322f2f;
        }

        .dark-mode .company-list-grid .MuiDataGrid-cell {
          color: #ffffff;
        }

        .dark-mode .company-list-grid .MuiDataGrid-row:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .dark-mode .company-list-grid .MuiDataGrid-row.table-danger {
          background: rgba(254, 124, 150, 0.12);
        }

        .dark-mode .company-list-grid .MuiDataGrid-row.table-success {
          background: rgba(27, 207, 180, 0.12);
        }

        .dark-mode .company-list-grid .MuiDataGrid-row.table-secondary {
          background: rgba(156, 159, 166, 0.12);
        }

        .dark-mode .company-list-grid .MuiDataGrid-row.table-danger:hover {
          background: rgba(254, 124, 150, 0.18);
        }

        .dark-mode .company-list-grid .MuiDataGrid-row.table-success:hover {
          background: rgba(27, 207, 180, 0.18);
        }

        .dark-mode .company-list-grid .MuiDataGrid-row.table-secondary:hover {
          background: rgba(156, 159, 166, 0.18);
        }

        .dark-mode .company-list-grid .MuiDataGrid-footerContainer {
          border-top-color: #322f2f;
          background: #1a191d;
        }

        .dark-mode .company-list-grid .MuiTablePagination-root,
        .dark-mode .company-list-grid .MuiTablePagination-caption,
        .dark-mode .company-list-grid .MuiTablePagination-selectIcon,
        .dark-mode .company-list-grid .MuiIconButton-root {
          color: #d8d8d8;
        }

        .dark-mode .company-list-grid .MuiDataGrid-columnSeparator {
          color: #322f2f;
        }

        .dark-mode .company-list-grid .MuiChip-root {
          border-color: transparent;
        }

        .dark-mode .company-list-grid .MuiChip-colorPrimary {
          background: rgba(182, 109, 255, 0.2);
          color: #f3ddff;
        }

        .dark-mode .company-list-grid .MuiChip-colorSecondary {
          background: rgba(254, 124, 150, 0.18);
          color: #ffd5dd;
        }

        .dark-mode .company-list-grid .MuiChip-default {
          background: rgba(156, 159, 166, 0.18);
          color: #f5f5f5;
        }

        .dark-mode .company-list-grid a {
          color: #d9b3ff;
        }
      `}</style>

      <div className="grid-card">
        <div className="grid-toolbar">
          <label className="search-box" htmlFor="company-search">
            <svg className="search-icon" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M12.9 14.32a8 8 0 111.414-1.414l4.387 4.387a1 1 0 01-1.414 1.414L12.9 14.32zM14 8a6 6 0 11-12 0 6 6 0 0112 0z"
                clipRule="evenodd"
              />
            </svg>
            <input
              id="company-search"
              className="search-field"
              type="search"
              placeholder="Search tickers, companies, industries, decisions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="search-clear"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                Clear
              </button>
            )}
          </label>
          <div className="search-meta">
            <strong>{filteredRows.length}</strong>
            of
            <strong>{rows.length}</strong>
            showing
          </div>
        </div>

        <div className="company-list-grid">
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSize={30}
            rowsPerPageOptions={[30]}
            autoHeight={true}
            autoPageSize={true}
            density={"compact"}
            disableColumnMenu
            getRowClassName={(params) =>
              (tableRowClass(params.getValue(params.id, 'recommendationColor')))
            }
          />
        </div>
      </div>
    </div>
  );
}

export default CompanyListDataGrid;
